import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { motion, AnimatePresence } from 'framer-motion';

const AnimatedFlowSection = ({ currentLanguage }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const content = {
    EN: {
      steps: [
        {
          icon: "UserPlus",
          title: "Sign up",
          description: "Complete our quick online application form",
          detail: "Provide basic information and verify your identity in minutes"
        },
        {
          icon: "CreditCard",
          title: "Activate card", 
          description: "Receive and activate your PSPXpress card",
          detail: "Your reloadable Mastercard® Prepaid Card arrives ready to use"
        },
        {
          icon: "ShoppingCart",
          title: "Use responsibly",
          description: "Make purchases and manage your spending",
          detail: "Use your card for everyday purchases while building good habits"
        },
        {
          icon: "BarChart3",
          title: "Credit activity may be reported monthly to Equifax",
          description: "Your responsible usage may help build credit history",
          detail: "Reporting is not guaranteed; results vary by individual"
        }
      ],
      disclaimer: "Reporting is not guaranteed; results vary."
    },
    FR: {
      steps: [
        {
          icon: "UserPlus",
          title: "S\'inscrire",
          description: "Remplissez notre formulaire de demande en ligne rapide",
          detail: "Fournissez des informations de base et vérifiez votre identité en quelques minutes"
        },
        {
          icon: "CreditCard",
          title: "Activer la carte",
          description: "Recevez et activez votre carte PSPXpress",
          detail: "Votre carte Mastercard® prépayée rechargeable arrive prête à utiliser"
        },
        {
          icon: "ShoppingCart",
          title: "Utiliser de manière responsable",
          description: "Effectuez des achats et gérez vos dépenses",
          detail: "Utilisez votre carte pour les achats quotidiens tout en développant de bonnes habitudes"
        },
        {
          icon: "BarChart3", 
          title: "L'activité de crédit peut être signalée mensuellement à Equifax",
          description: "Votre utilisation responsable peut aider à construire un historique de crédit",
          detail: "Le signalement n\'est pas garanti; les résultats varient selon l\'individu"
        }
      ],
      disclaimer: "Le signalement n\'est pas garanti; les résultats varient."
    }
  };

  const text = content?.[currentLanguage] || content?.EN;

  // Auto-advance steps
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % text?.steps?.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [text?.steps?.length]);

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-12">
            <div className="flex justify-between items-center mb-4">
              {text?.steps?.map((_, index) => (
                <div
                  key={index}
                  className={`w-3 h-3 rounded-full transition-colors duration-500 ${
                    index <= currentStep ? 'bg-primary' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
            <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-primary"
                initial={{ width: '0%' }}
                animate={{ width: `${((currentStep + 1) / text?.steps?.length) * 100}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>

          {/* Animated Steps */}
          <div className="relative min-h-[400px] flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                className="text-center max-w-2xl"
              >
                {/* Icon */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                  className="w-24 h-24 bg-primary rounded-full flex items-center justify-center mx-auto mb-8"
                >
                  <Icon name={text?.steps?.[currentStep]?.icon} size={40} className="text-white" />
                </motion.div>

                {/* Step Number */}
                <div className="text-sm text-primary font-semibold mb-2">
                  Step {currentStep + 1} of {text?.steps?.length}
                </div>

                {/* Title */}
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  {text?.steps?.[currentStep]?.title}
                </h3>

                {/* Description */}
                <p className="text-xl text-gray-600 mb-4">
                  {text?.steps?.[currentStep]?.description}
                </p>

                {/* Detail */}
                <p className="text-gray-500">
                  {text?.steps?.[currentStep]?.detail}
                </p>

                {/* Special disclaimer for last step */}
                {currentStep === text?.steps?.length - 1 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg"
                  >
                    <div className="flex items-start space-x-2">
                      <Icon name="AlertTriangle" size={20} className="text-amber-600 mt-0.5" />
                      <p className="text-sm text-amber-800">
                        {text?.disclaimer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Dots */}
          <div className="flex justify-center space-x-2 mt-8">
            {text?.steps?.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                  index === currentStep ? 'bg-primary' : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AnimatedFlowSection;